import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Database,
  File,
  FileArchive,
  FileJson,
  FileSearch,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderTree,
  History,
  Image as ImageIcon,
} from "lucide-react";

import { DatasetBreadcrumbNav } from "../../components/DatasetBreadcrumbNav";
import {
  dataKeys,
  changeSourceLogicalPath,
  createDraftFromRevision,
  getSources,
  getRevisions,
  getSourceExplorerContent,
  getSourceExplorerFiles,
  getSourceExplorerMetadata,
  getSourceExplorerPreview,
  getSourceExplorerTree,
  moveSource,
  removeSource,
  safeErrorMessage,
  uploadSources,
  type DatasetLocation,
} from "./api";
import { formatSourceBytes, formatSourceDate } from "./source-explorer-model";
import { SourceExplorer, type SourceImportSelection } from "./SourceExplorer";
import type {
  SourceExplorerFile,
  SourceExplorerTree,
  SourceFilePreview,
  SourceRevision,
  SourceRoleContract,
} from "./types";


const PAGE_SIZE = 200;

type ExplorerEditAction =
  | { kind: "replace"; source: SourceExplorerFile; file: File }
  | { kind: "move"; source: SourceExplorerFile; role: string }
  | { kind: "rename"; source: SourceExplorerFile; virtualPath: string }
  | { kind: "remove"; sources: SourceExplorerFile[] };

export function SourceRevisionExplorerPage() {
  const { clientId, datasetId } = useParams<{ clientId: string; datasetId: string }>();
  if (!clientId || !datasetId) return <p className="dm-error">Dataset introuvable.</p>;
  const location = { clientId, datasetId };
  return <div className="dataset-detail-page dd-page-wrapper">
    <DatasetBreadcrumbNav
      clientId={clientId}
      clientLabel={clientId}
      datasetId={datasetId}
      datasetLabel={datasetId}
      currentLabel="Explorateur de sources"
    />
    <SourceRevisionExplorer location={location} />
  </div>;
}

export function SourceRevisionExplorer({ location, embedded = false }: { location: DatasetLocation; embedded?: boolean }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const revisionsQuery = useQuery({
    queryKey: dataKeys.revisions(location),
    queryFn: () => getRevisions(location),
    staleTime: 60_000,
  });
  const revisions = revisionsQuery.data ?? [];
  const requestedRevision = searchParams.get("revision") ?? "";
  const selectedRevision = revisionById(revisions, requestedRevision);
  const revisionId = selectedRevision?.id ?? "";
  const folder = searchParams.get("path") ?? "";
  const search = searchParams.get("search") ?? "";
  const type = searchParams.get("type") ?? "";
  const role = searchParams.get("role") ?? "";
  const sort = searchParams.get("sort") ?? "name";
  const order = searchParams.get("order") ?? "asc";
  const offset = validOffset(searchParams.get("offset"));
  const selectedFileId = searchParams.get("file") ?? "";
  const [searchInput, setSearchInput] = useState(search);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewWide, setPreviewWide] = useState(false);
  const [sheet, setSheet] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pendingImport, setPendingImport] = useState<SourceImportSelection | null>(null);
  const [dropError, setDropError] = useState("");
  const [openEditorAfterDraft, setOpenEditorAfterDraft] = useState(false);

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === search) return;
      updateExplorerParams(setSearchParams, searchParams, { search: searchInput, offset: "" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, searchInput, searchParams, setSearchParams]);
  useEffect(() => {
    if (!revisions.length || requestedRevision) return;
    const firstId = revisions[0].id;
    if (!firstId) return;
    updateExplorerParams(setSearchParams, searchParams, { revision: firstId });
  }, [requestedRevision, revisions, searchParams, setSearchParams]);
  useEffect(() => {
    setSelectedIds(new Set());
    setSheet("");
  }, [folder, revisionId]);
  useEffect(() => setSheet(""), [selectedFileId]);
  useEffect(() => {
    setEditMode(false);
    setPendingImport(null);
    setOpenEditorAfterDraft(false);
  }, [location.clientId, location.datasetId]);

  const treeQuery = useQuery({
    queryKey: dataKeys.explorerTree(location, revisionId),
    queryFn: () => getSourceExplorerTree(location, revisionId),
    enabled: Boolean(revisionId),
    staleTime: selectedRevision?.editable ? 10_000 : Infinity,
  });
  const fileQueryString = JSON.stringify({ folder, search, type, role, sort, order, offset });
  const filesQuery = useQuery({
    queryKey: dataKeys.explorerFiles(location, revisionId, fileQueryString),
    queryFn: () => getSourceExplorerFiles(location, revisionId, {
      path: folder, search, type, role, sort, order, offset, limit: PAGE_SIZE,
    }),
    enabled: Boolean(revisionId),
    staleTime: selectedRevision?.editable ? 10_000 : Infinity,
    placeholderData: (previous) => previous,
  });
  const metadataQuery = useQuery({
    queryKey: dataKeys.explorerMetadata(location, revisionId, selectedFileId),
    queryFn: ({ signal }) => getSourceExplorerMetadata(location, revisionId, selectedFileId, signal),
    enabled: Boolean(revisionId && selectedFileId),
    staleTime: selectedRevision?.editable ? 10_000 : Infinity,
  });
  const previewQuery = useQuery({
    queryKey: dataKeys.explorerPreview(location, revisionId, selectedFileId, sheet),
    queryFn: ({ signal }) => getSourceExplorerPreview(location, revisionId, selectedFileId, sheet, signal),
    enabled: Boolean(revisionId && selectedFileId && metadataQuery.data?.preview.supported && previewOpen),
    staleTime: selectedRevision?.editable ? 10_000 : Infinity,
    retry: false,
  });
  const canEdit = Boolean(treeQuery.data?.revision.canEdit);
  const sourcesQuery = useQuery({
    queryKey: dataKeys.sources(location),
    queryFn: () => getSources(location),
    enabled: editMode && canEdit,
    staleTime: 0,
  });
  useEffect(() => {
    if (!editMode || canEdit) return;
    setEditMode(false);
  }, [canEdit, editMode]);
  useEffect(() => {
    if (!canEdit) return;
    if (!pendingImport && !openEditorAfterDraft) return;
    setEditMode(true);
    setOpenEditorAfterDraft(false);
  }, [canEdit, openEditorAfterDraft, pendingImport]);

  const refreshExplorer = async () => {
    const nextRevisions = await getRevisions(location);
    queryClient.setQueryData(dataKeys.revisions(location), nextRevisions);
    const current = nextRevisions.find((revision) => revision.editable && revision.id);
    await queryClient.invalidateQueries({ queryKey: dataKeys.root(location) });
    if (current?.id) {
      updateExplorerParams(setSearchParams, searchParams, {
        revision: current.id,
        file: "",
        offset: "",
        mode: "read",
      });
    }
    setSelectedIds(new Set());
  };
  const editMutation = useMutation({
    mutationFn: async (action: ExplorerEditAction) => {
      const data = sourcesQuery.data;
      if (!data) throw new Error("La préparation modifiable n’est pas chargée.");
      if (action.kind === "replace") {
        return await uploadSources(location, [action.file], new AbortController().signal, action.source.role, data.sourceRevision, action.source.id);
      }
      if (action.kind === "move") {
        return await moveSource(location, action.source.id, action.role, data.sourceRevision);
      }
      if (action.kind === "rename") {
        return await changeSourceLogicalPath(location, action.source.id, action.virtualPath, data.sourceRevision);
      }
      let expectedRevision = data.sourceRevision;
      for (const source of action.sources) {
        await removeSource(location, source.id, expectedRevision);
        expectedRevision += 1;
      }
      return undefined;
    },
    onSuccess: refreshExplorer,
  });
  const createDraftMutation = useMutation({
    mutationFn: () => createDraftFromRevision(location, revisionId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: dataKeys.root(location) });
      updateExplorerParams(setSearchParams, searchParams, {
        revision: result.revisionId,
        path: "",
        file: "",
        offset: "",
      });
    },
    onError: () => setOpenEditorAfterDraft(false),
  });

  const selectFolder = (path: string) => {
    updateExplorerParams(setSearchParams, searchParams, { path, file: "", offset: "" });
  };
  const selectFile = (file: SourceExplorerFile) => {
    updateExplorerParams(setSearchParams, searchParams, { file: file.id });
    setPreviewOpen(true);
  };
  const acceptDroppedFiles = (files: File[]) => {
    if (!files.length) return;
    const importSelection = {
      files,
      role: sourceRoleForFolder(treeQuery.data, folder),
      mode: droppedImportMode(files),
    };
    setDropError("");
    setPendingImport(importSelection);
    if (canEdit) {
      setEditMode(true);
      return;
    }
    if (treeQuery.data?.revision.canCreateDraft) {
      setOpenEditorAfterDraft(true);
      createDraftMutation.mutate();
      return;
    }
    setPendingImport(null);
    setDropError("L’ajout de données est temporairement indisponible pendant le traitement en cours.");
  };
  if (revisionsQuery.isLoading) return <main className="dm-page"><p>Chargement des révisions…</p></main>;
  if (revisionsQuery.error) return <main className="dm-page"><p className="dm-error">{safeErrorMessage(revisionsQuery.error)}</p></main>;
  if (!revisions.length) return <main className="dm-page"><p className="dm-empty">Aucune révision de sources.</p></main>;

  const Root = embedded ? "section" : "main";
  const physicalFileCount = explorerPhysicalFileCount(treeQuery.data);
  return <Root className="dm-page source-revision-page">
    <header className="source-explorer-page-header">
      <div>
        <Link className="btn btn-outline btn-sm source-explorer-back-link" to={`/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}/data-management?section=sources`}>
          <ArrowLeft size={16} aria-hidden="true" /> Retour à la synthèse
        </Link>
        <h1>Explorateur de sources</h1>
        <p>Navigation logique sécurisée, indépendante des clés physiques de stockage.</p>
      </div>
      <RevisionSelector revisions={revisions} selectedId={revisionId} selectedFileCount={physicalFileCount} onSelect={(id) => {
        setEditMode(false);
        setPendingImport(null);
        setOpenEditorAfterDraft(false);
        updateExplorerParams(setSearchParams, searchParams, { revision: id, path: "", file: "", offset: "" });
      }} />
    </header>
    <ExplorerViewNavigation location={location} />
    {treeQuery.data ? <RevisionBanner revision={treeQuery.data.revision} editMode={editMode} /> : null}
    {treeQuery.data ? <ExplorerModeActions
      revision={treeQuery.data.revision}
      editMode={editMode}
      busy={createDraftMutation.isPending}
      onEdit={() => setEditMode(true)}
      onLeave={() => {
        setEditMode(false);
        setPendingImport(null);
        setOpenEditorAfterDraft(false);
      }}
      onCreateDraft={() => {
        setOpenEditorAfterDraft(true);
        createDraftMutation.mutate();
      }}
    /> : null}
    {createDraftMutation.error ? <p className="dm-error" role="alert">{safeErrorMessage(createDraftMutation.error)}</p> : null}
    {dropError ? <p className="dm-error" role="alert">{dropError}</p> : null}
    {editMode && sourcesQuery.data ? <SourceExplorer
      location={location}
      data={sourcesQuery.data}
      revisions={revisions}
      display="editor"
      initialImport={pendingImport}
      onChanged={refreshExplorer}
    /> : null}
    <div
      className={`source-revision-explorer${previewOpen ? "" : " preview-collapsed"}${previewWide ? " preview-wide" : ""}${dragActive ? " is-dragging" : ""}`}
      data-testid="source-explorer-drop-target"
      onDragEnter={(event) => {
        if (!containsDraggedFiles(event.dataTransfer)) return;
        event.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(event) => {
        if (!containsDraggedFiles(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(event) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
        setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        acceptDroppedFiles(Array.from(event.dataTransfer.files));
      }}
    >
      {dragActive ? <div className="source-explorer-drop-overlay" role="status">Déposez les fichiers pour les ajouter</div> : null}
      <ExplorerTree tree={treeQuery.data} selectedPath={folder} loading={treeQuery.isLoading} onSelect={selectFolder} />
      <section className="source-explorer-list-panel" aria-label="Contenu du dossier">
        <ExplorerToolbar
          tree={treeQuery.data}
          path={folder}
          search={searchInput}
          type={type}
          role={role}
          sort={sort}
          order={order}
          onPath={selectFolder}
          onSearch={setSearchInput}
          onChange={(changes) => updateExplorerParams(setSearchParams, searchParams, { ...changes, offset: "" })}
        />
        {editMode ? <EditToolbar
          selectedCount={selectedIds.size}
          busy={editMutation.isPending || sourcesQuery.isLoading}
          onRemove={() => {
            const sources = filesQuery.data?.items.filter((item) => selectedIds.has(item.id)) ?? [];
            if (sources.length) editMutation.mutate({ kind: "remove", sources });
          }}
        /> : null}
        {editMutation.error ? <p className="dm-error" role="alert">{safeErrorMessage(editMutation.error)}</p> : null}
        {filesQuery.isLoading ? <ExplorerSkeleton /> : null}
        {filesQuery.error ? <p className="dm-error">{safeErrorMessage(filesQuery.error)}</p> : null}
        {filesQuery.data ? <ExplorerContents
          tree={treeQuery.data}
          path={folder}
          search={search}
          files={filesQuery.data.items}
          fileTotal={filesQuery.data.total}
          selectedIds={selectedIds}
          editMode={editMode}
          roles={sourcesQuery.data?.contract?.roles ?? []}
          busy={editMutation.isPending}
          onFolder={selectFolder}
          onFile={selectFile}
          onToggle={(file) => toggleSelectedFile(setSelectedIds, file.id)}
          onReplace={(source, file) => editMutation.mutate({ kind: "replace", source, file })}
          onMove={(source, nextRole) => editMutation.mutate({ kind: "move", source, role: nextRole })}
          onRename={(source) => {
            const virtualPath = window.prompt("Nouveau chemin logique", source.logicalPath);
            if (virtualPath && virtualPath !== source.logicalPath) editMutation.mutate({ kind: "rename", source, virtualPath });
          }}
          onRemove={(source) => editMutation.mutate({ kind: "remove", sources: [source] })}
        /> : null}
        {filesQuery.data ? <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={filesQuery.data.total}
          onPage={(next) => updateExplorerParams(setSearchParams, searchParams, { offset: `${next}` })}
        /> : null}
      </section>
      <PreviewPanel
        open={previewOpen}
        metadata={metadataQuery.data}
        metadataLoading={metadataQuery.isLoading}
        preview={previewQuery.data}
        previewLoading={previewQuery.isLoading}
        previewError={previewQuery.error}
        location={location}
        revisionId={revisionId}
        canDownload={Boolean(treeQuery.data?.revision.canDownload)}
        sheet={sheet}
        wide={previewWide}
        onSheet={setSheet}
        onToggle={() => {
          setPreviewOpen((value) => !value);
          setPreviewWide(false);
        }}
        onWide={() => setPreviewWide((value) => !value)}
      />
    </div>
  </Root>;
}

function RevisionSelector({ revisions, selectedId, selectedFileCount, onSelect }: {
  revisions: SourceRevision[]; selectedId: string; selectedFileCount?: number; onSelect: (id: string) => void;
}) {
  return <label id="source-revision-selector" className="source-revision-selector">Révision
    <select value={selectedId} onChange={(event) => onSelect(event.target.value)}>
      {revisions.filter((revision) => revision.id).map((revision) => {
        const count = revisionFileCount(revision, selectedId, selectedFileCount);
        return <option key={revision.id} value={revision.id}>
          r{revision.sourceRevision} · {revision.editable ? "Préparation modifiable" : "Lecture seule"} · {fileCountLabel(count)}
        </option>;
      })}
    </select>
  </label>;
}

function ExplorerViewNavigation({ location }: { location: DatasetLocation }) {
  const base = `/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}`;
  return <nav className="source-explorer-view-navigation" aria-label="Vues des sources" role="tablist">
    <span className="source-explorer-tab is-active" role="tab" aria-current="page" aria-selected="true">
      <FolderTree size={17} aria-hidden="true" /> Explorateur
    </span>
    <Link className="source-explorer-tab" role="tab" aria-selected="false" to={`${base}/data-management?section=sources&view=summary`}>
      <FileText size={17} aria-hidden="true" /> Résumé
    </Link>
    <a className="source-explorer-tab" role="tab" aria-selected="false" href="#source-revision-selector">
      <History size={17} aria-hidden="true" /> Historique des révisions
    </a>
  </nav>;
}

function RevisionBanner({ revision, editMode }: { revision: SourceExplorerTree["revision"]; editMode: boolean }) {
  const label = editMode ? "Mode modification" : "lecture seule";
  return <div className={`source-revision-banner${editMode ? " is-editable" : " is-readonly"}`} role="status">
    <strong>Révision r{revision.number} — {label}</strong>
    <span>Créée depuis la préparation {revision.draftNumber} · {revisionStatusLabel(revision.status)}</span>
  </div>;
}

function ExplorerModeActions({ revision, editMode, busy, onEdit, onLeave, onCreateDraft }: {
  revision: SourceExplorerTree["revision"];
  editMode: boolean;
  busy: boolean;
  onEdit: () => void;
  onLeave: () => void;
  onCreateDraft: () => void;
}) {
  if (editMode) return <div className="source-explorer-mode-actions">
    <p><strong>Mode modification — les changements créeront une nouvelle révision immuable.</strong></p>
    <button className="btn btn-outline" type="button" onClick={onLeave}>Quitter le mode modification</button>
  </div>;
  if (revision.canEdit) return <div className="source-explorer-mode-actions">
    <button className="btn btn-primary" type="button" onClick={onEdit}>Modifier les sources</button>
    <span className="source-explorer-drop-help">ou glissez-déposez vos fichiers dans l’explorateur</span>
  </div>;
  if (revision.canCreateDraft) return <div className="source-explorer-mode-actions">
    <button className="btn btn-primary" type="button" disabled={busy} onClick={onCreateDraft}>
      {busy ? "Préparation de l’import…" : "Ajouter des données"}
    </button>
    <span className="source-explorer-drop-help">ou glissez-déposez vos fichiers dans l’explorateur</span>
  </div>;
  return null;
}

function EditToolbar({ selectedCount, busy, onRemove }: {
  selectedCount: number;
  busy: boolean;
  onRemove: () => void;
}) {
  return <div className="source-explorer-edit-toolbar" aria-label="Actions de modification">
    <button className="btn btn-danger btn-sm" type="button" disabled={busy || selectedCount === 0} onClick={onRemove}>Retirer la sélection</button>
    <span>{selectedCount} sélectionné(s)</span>
  </div>;
}

function ExplorerTree({ tree, selectedPath, loading, onSelect }: {
  tree: SourceExplorerTree | undefined; selectedPath: string; loading: boolean; onSelect: (path: string) => void;
}) {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!selectedPath) return;
    const parts = selectedPath.split("/");
    setExpandedPaths((current) => {
      const next = new Set(current);
      for (let index = 1; index <= parts.length; index += 1) {
        next.add(parts.slice(0, index).join("/"));
      }
      return next;
    });
  }, [selectedPath]);
  const setExpanded = (path: string, expanded: boolean) => {
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (expanded) next.add(path);
      else next.delete(path);
      return next;
    });
  };
  const rootFileCount = explorerPhysicalFileCount(tree);
  return <nav className="source-explorer-tree" aria-label="Arborescence des sources">
    <h2><FolderTree size={18} aria-hidden="true" /> Sources</h2>
    <button type="button" className={`source-tree-row${!selectedPath ? " is-selected" : ""}`} aria-current={!selectedPath ? "page" : undefined} onClick={() => onSelect("")}>
      <FolderTree className="source-tree-icon" size={17} aria-hidden="true" />
      <span>Tous les dossiers</span>
      <span className="source-tree-selection-indicator" aria-hidden="true" />
      <small>{fileCountLabel(rootFileCount)}</small>
    </button>
    {loading ? <p>Chargement…</p> : null}
    {tree ? <FolderBranch tree={tree} parentPath="" selectedPath={selectedPath} expandedPaths={expandedPaths} onSelect={onSelect} onExpanded={setExpanded} /> : null}
  </nav>;
}

function FolderBranch({ tree, parentPath, selectedPath, expandedPaths, onSelect, onExpanded }: {
  tree: SourceExplorerTree; parentPath: string; selectedPath: string;
  expandedPaths: ReadonlySet<string>; onSelect: (path: string) => void;
  onExpanded: (path: string, expanded: boolean) => void;
}) {
  const folders = tree.folders;
  const children = folders.filter((folder) => folder.parentPath === parentPath);
  if (!children.length) return null;
  return <ul role="group">{children.map((folder) => {
    const hasChildren = folders.some((candidate) => candidate.parentPath === folder.path);
    const expanded = expandedPaths.has(folder.path);
    const selected = selectedPath === folder.path;
    const openFolder = () => {
      onSelect(folder.path);
      if (hasChildren) onExpanded(folder.path, true);
    };
    return <li key={folder.path}>
      <button
        type="button"
        className={`source-tree-row${selected ? " is-selected" : ""}${expanded ? " is-expanded" : ""}`}
        aria-current={selected ? "page" : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        onClick={openFolder}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            openFolder();
          }
          if (event.key === "ArrowRight" && hasChildren) {
            event.preventDefault();
            onExpanded(folder.path, true);
          }
          if (event.key === "ArrowLeft" && hasChildren) {
            event.preventDefault();
            onExpanded(folder.path, false);
          }
        }}
      >
        {expanded ? <FolderOpen className="source-tree-icon" size={17} aria-hidden="true" /> : <Folder className="source-tree-icon" size={17} aria-hidden="true" />}
        <span>{folder.name}</span>
        <span className="source-tree-selection-indicator" aria-hidden="true" />
        <small>{folderCounterLabel(tree, folder)}</small>
      </button>
      {expanded ? <FolderBranch tree={tree} parentPath={folder.path} selectedPath={selectedPath} expandedPaths={expandedPaths} onSelect={onSelect} onExpanded={onExpanded} /> : null}
    </li>;
  })}</ul>;
}

function ExplorerToolbar({ tree, path, search, type, role, sort, order, onPath, onSearch, onChange }: {
  tree: SourceExplorerTree | undefined; path: string; search: string; type: string; role: string;
  sort: string; order: string; onPath: (path: string) => void; onSearch: (value: string) => void;
  onChange: (changes: Record<string, string>) => void;
}) {
  const crumbs = path.split("/").filter(Boolean);
  return <div className="source-explorer-toolbar">
    <nav className="source-explorer-breadcrumb" aria-label="Dossier courant">
      <button className="btn btn-ghost btn-sm" type="button" onClick={() => onPath("")}>Sources</button>
      {crumbs.map((crumb, index) => {
        const target = crumbs.slice(0, index + 1).join("/");
        return <span key={target}> / <button className="btn btn-ghost btn-sm" type="button" onClick={() => onPath(target)}>{crumb}</button></span>;
      })}
    </nav>
    <div className="source-explorer-filters">
      <label>Rechercher<input value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Nom ou chemin" /></label>
      <label>Type<select value={type} onChange={(event) => onChange({ type: event.target.value })}>
        <option value="">Tous</option><option value="csv">CSV</option><option value="tsv">TSV</option><option value="xlsx">XLSX</option><option value="json">JSON</option><option value="pdf">PDF</option><option value="png">Image PNG</option><option value="zip">ZIP</option>
      </select></label>
      <label>Rôle<select value={role} onChange={(event) => onChange({ role: event.target.value })}>
        <option value="">Tous</option>{tree?.roles.map((item) => <option key={item.role} value={item.role}>{item.label}</option>)}
      </select></label>
      <label>Tri<select value={`${sort}:${order}`} onChange={(event) => {
        const [nextSort, nextOrder] = event.target.value.split(":");
        onChange({ sort: nextSort, order: nextOrder });
      }}><option value="name:asc">Nom A–Z</option><option value="name:desc">Nom Z–A</option><option value="date:desc">Plus récents</option><option value="size:desc">Plus volumineux</option><option value="type:asc">Type</option><option value="role:asc">Rôle</option></select></label>
    </div>
  </div>;
}

function SelectionBar({ count, readOnly }: { count: number; readOnly: boolean }) {
  return <div className="source-explorer-selection-bar"><span>{count} sélectionné(s)</span><span>{readOnly ? "Révision en lecture seule" : "Les modifications passent par la préparation existante"}</span></div>;
}

function ExplorerContents({ tree, path, search, files, fileTotal, selectedIds, editMode, roles, busy, onFolder, onFile, onToggle, onReplace, onMove, onRename, onRemove }: {
  tree: SourceExplorerTree | undefined; path: string; search: string; files: SourceExplorerFile[];
  fileTotal: number;
  selectedIds: ReadonlySet<string>; editMode: boolean; roles: SourceRoleContract[]; busy: boolean;
  onFolder: (path: string) => void; onFile: (file: SourceExplorerFile) => void;
  onToggle: (file: SourceExplorerFile) => void;
  onReplace: (source: SourceExplorerFile, file: File) => void;
  onMove: (source: SourceExplorerFile, role: string) => void;
  onRename: (source: SourceExplorerFile) => void;
  onRemove: (source: SourceExplorerFile) => void;
}) {
  const folders = search ? [] : tree?.folders.filter((folder) => folder.parentPath === path) ?? [];
  const systemResources = !path && !search ? explorerSystemResources(tree) : [];
  if (!folders.length && !files.length && !systemResources.length) {
    return <div className="source-explorer-content-empty"><FileSearch size={30} aria-hidden="true" /><strong>Dossier vide</strong><p>Ce dossier ne contient aucun fichier pour ces filtres.</p></div>;
  }
  return <div className="source-explorer-contents">
    {folders.length ? <section className="source-explorer-content-group" aria-labelledby="logical-folders-title">
      <h3 id="logical-folders-title"><Folder size={16} aria-hidden="true" /> Dossiers logiques <span>{folders.length}</span></h3>
      {folders.map((folder) => {
        const resourceCount = systemResourceCountForFolder(tree, folder.path);
        const fileCount = folderPhysicalFileCount(tree, folder);
        const size = fileCount === 0 && resourceCount > 0 ? "—" : formatSourceBytes(Number(folder.totalBytes));
        return <button key={folder.path} type="button" className="source-explorer-folder-row" onDoubleClick={() => onFolder(folder.path)} onClick={() => onFolder(folder.path)}>
          <Folder size={18} aria-hidden="true" /><strong>{folder.name}</strong><span>{folderCounterLabel(tree, folder)}</span><span>{size}</span>
        </button>;
      })}
    </section> : null}
    {files.length ? <section className="source-explorer-content-group" aria-labelledby="manifest-files-title">
      <h3 id="manifest-files-title"><File size={16} aria-hidden="true" /> Fichiers du manifeste <span>{fileTotal}</span></h3>
      <VirtualExplorerFiles
        files={files}
        selectedIds={selectedIds}
        editMode={editMode}
        roles={roles}
        busy={busy}
        scrollKey={`${tree?.revision.id ?? "revision"}:${path}:${search}`}
        onFile={onFile}
        onToggle={onToggle}
        onReplace={onReplace}
        onMove={onMove}
        onRename={onRename}
        onRemove={onRemove}
      />
    </section> : null}
    {systemResources.length ? <section className="source-explorer-content-group source-system-resource-group" aria-labelledby="system-resources-title">
      <h3 id="system-resources-title"><Database size={16} aria-hidden="true" /> Ressources système <span>{systemResourceCountLabel(systemResources.length)}</span></h3>
      {systemResources.map((resource) => <div className="source-system-resource-row" key={resource.id}>
        <Database size={18} aria-hidden="true" />
        <strong>{resource.name}</strong>
        <span>Ressource système</span>
        <span>{resource.logicalFolder}</span>
        <span aria-label="Aucun objet physique">—</span>
      </div>)}
    </section> : null}
  </div>;
}

function VirtualExplorerFiles({ files, selectedIds, editMode, roles, busy, scrollKey, onFile, onToggle, onReplace, onMove, onRename, onRemove }: {
  files: SourceExplorerFile[]; selectedIds: ReadonlySet<string>;
  editMode: boolean; roles: SourceRoleContract[]; busy: boolean; scrollKey: string;
  onFile: (file: SourceExplorerFile) => void; onToggle: (file: SourceExplorerFile) => void;
  onReplace: (source: SourceExplorerFile, file: File) => void;
  onMove: (source: SourceExplorerFile, role: string) => void;
  onRename: (source: SourceExplorerFile) => void;
  onRemove: (source: SourceExplorerFile) => void;
}) {
  const viewport = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(() => storedExplorerScroll(scrollKey));
  useEffect(() => {
    const next = storedExplorerScroll(scrollKey);
    setScrollTop(next);
    if (viewport.current) viewport.current.scrollTop = next;
  }, [scrollKey]);
  const viewportHeight = 560;
  const rowHeight = 54;
  const count = Math.ceil(viewportHeight / rowHeight) + 6;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 3);
  const visible = files.slice(start, start + count);
  const userRoles = roles.filter((item) => item.providedBy === "user" && item.compatible !== false);
  return <div ref={viewport} className="source-explorer-file-viewport" data-testid="source-explorer-file-list" style={{ height: viewportHeight }} onScroll={(event) => {
    const value = event.currentTarget.scrollTop;
    setScrollTop(value);
    storeExplorerScroll(scrollKey, value);
  }}>
    <div style={{ height: files.length * rowHeight, position: "relative" }}>
      {visible.map((file, index) => <div
        key={file.id}
        className={`source-explorer-file-row${editMode && !file.readOnly ? " is-editable" : ""}`}
        style={{ position: "absolute", top: (start + index) * rowHeight, height: rowHeight }}
        tabIndex={0}
        onDoubleClick={() => onFile(file)}
        onKeyDown={(event) => { if (event.key === "Enter") onFile(file); }}
      >
        {editMode && !file.readOnly ? <label><input type="checkbox" checked={selectedIds.has(file.id)} onChange={() => onToggle(file)} /><span className="sr-only">Sélectionner {file.name}</span></label> : <FileTypeIcon filename={file.name} />}
        <button type="button" className="source-explorer-file-name" onClick={() => onFile(file)}><strong>{file.name}</strong><span>{file.logicalPath}</span></button>
        <span>{file.role}</span><span>{formatSourceBytes(Number(file.sizeBytes))}</span><span>{formatSourceDate(file.createdAt)}</span>
        {editMode && !file.readOnly ? <details className="source-explorer-context"><summary>Modifier</summary>
          <div className="source-explorer-context-menu">
            <label>Remplacer<input className="source-hidden-input" type="file" disabled={busy} onChange={(event) => {
              const replacement = event.target.files?.[0];
              if (replacement) onReplace(file, replacement);
              event.target.value = "";
            }} /></label>
            <label>Déplacer<select value={file.role} disabled={busy} onChange={(event) => onMove(file, event.target.value)}>{userRoles.map((item) => <option key={item.role} value={item.role}>{item.label}</option>)}</select></label>
            <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => onRename(file)}>Renommer logiquement</button>
            <button className="btn btn-danger btn-sm" type="button" disabled={busy} onClick={() => onRemove(file)}>Retirer</button>
          </div>
        </details> : null}
      </div>)}
    </div>
  </div>;
}

function PreviewPanel({ open, wide, metadata, metadataLoading, preview, previewLoading, previewError, location, revisionId, canDownload, sheet, onSheet, onToggle, onWide }: {
  open: boolean; metadata: Awaited<ReturnType<typeof getSourceExplorerMetadata>> | undefined; metadataLoading: boolean;
  preview: SourceFilePreview | undefined; previewLoading: boolean; previewError: unknown;
  location: DatasetLocation; revisionId: string; canDownload: boolean; sheet: string; wide: boolean;
  onSheet: (sheet: string) => void; onToggle: () => void; onWide: () => void;
}) {
  if (!open) return <aside className="source-explorer-preview-collapsed"><button className="btn btn-ghost btn-sm" type="button" onClick={onToggle}>Afficher l’aperçu</button></aside>;
  return <aside className="source-explorer-preview" aria-label="Aperçu du fichier">
    <header><h2><FileSearch size={18} aria-hidden="true" /> Aperçu</h2><div className="source-preview-actions">
      {metadata?.preview.supported ? <button className="btn btn-ghost btn-sm" type="button" aria-pressed={wide} onClick={onWide}>{wide ? "Réduire l’aperçu" : "Agrandir l’aperçu"}</button> : null}
      <button className="btn btn-ghost btn-sm" type="button" onClick={onToggle}>Replier</button>
    </div></header>
    {metadataLoading ? <PreviewSkeleton label="Chargement des métadonnées" /> : null}
    {!metadata && !metadataLoading ? <div className="source-preview-empty" role="status">
      <FileSearch size={38} aria-hidden="true" />
      <strong>Aucun fichier sélectionné</strong>
      <p>Sélectionnez un fichier dans la liste pour afficher ses métadonnées et son aperçu.</p>
    </div> : null}
    {metadata ? <FileMetadata metadata={metadata} location={location} revisionId={revisionId} canDownload={canDownload} /> : null}
    {previewLoading ? <PreviewSkeleton label="Chargement de l’aperçu" /> : null}
    {previewError ? <p className="dm-warning" role="alert">{safeErrorMessage(previewError)}</p> : null}
    {preview ? <PreviewContent preview={preview} metadata={metadata} location={location} revisionId={revisionId} sheet={sheet} onSheet={onSheet} /> : null}
  </aside>;
}

function FileMetadata({ metadata, location, revisionId, canDownload }: {
  metadata: Awaited<ReturnType<typeof getSourceExplorerMetadata>>; location: DatasetLocation; revisionId: string; canDownload: boolean;
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  useEffect(() => {
    setDownloading(false);
    setDownloadError("");
  }, [metadata.id]);
  const download = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const blob = await getSourceExplorerContent(location, revisionId, metadata.id, "attachment");
      saveBlob(blob, safeDownloadName(metadata.name));
    } catch (error) {
      setDownloadError(safeErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };
  return <><dl className="source-explorer-metadata"><dt>Nom</dt><dd>{metadata.name}</dd><dt>Rôle</dt><dd>{metadata.role}</dd><dt>Type MIME</dt><dd>{metadata.mimeType ?? "Non renseigné"}</dd><dt>Taille</dt><dd>{formatSourceBytes(Number(metadata.sizeBytes))}</dd><dt>Checksum</dt><dd>{metadata.checksum ?? "Non disponible"}</dd><dt>Chemin logique</dt><dd>{metadata.logicalPath}</dd><dt>Révision</dt><dd>r{metadata.revisionNumber} · {metadata.revisionStatus}</dd><dt>Origine</dt><dd>{metadata.systemProvided ? "Fourni par le système" : "Fichier importé"}</dd></dl>
    {!metadata.systemProvided && canDownload ? <button className="btn btn-outline" type="button" disabled={downloading} onClick={() => void download()}>{downloading ? "Téléchargement…" : "Télécharger"}</button> : null}
    {downloadError ? <p className="dm-warning" role="alert">Téléchargement impossible : {downloadError}</p> : null}
    {metadata.systemProvided ? <p className="dm-guidance">Fichier système en lecture seule.</p> : null}</>;
}

function PreviewContent({ preview, metadata, location, revisionId, sheet, onSheet }: {
  preview: SourceFilePreview; metadata: Awaited<ReturnType<typeof getSourceExplorerMetadata>> | undefined;
  location: DatasetLocation; revisionId: string; sheet: string; onSheet: (sheet: string) => void;
}) {
  if (preview.type === "csv") return <TabularPreview preview={preview} />;
  if (preview.type === "spreadsheet") return <><label>Feuille XLSX<select value={sheet || preview.activeSheet} onChange={(event) => onSheet(event.target.value)}>{preview.sheets.map((name) => <option key={name}>{name}</option>)}</select></label><TabularPreview preview={preview} /></>;
  if (preview.type === "json") return <><JsonNode value={preview.value} name="document" /><details><summary>Vue texte</summary><pre>{preview.text}</pre></details>{preview.truncated ? <p className="dm-warning">Document tronqué à la profondeur {preview.maxDepth}.</p> : null}</>;
  if (preview.type === "text") return <><p>Encodage {preview.encoding}{preview.markdown ? " · Markdown sécurisé — HTML brut désactivé" : ""}</p><pre>{preview.text}</pre>{preview.truncated ? <p className="dm-warning">Extrait tronqué.</p> : null}</>;
  if (preview.type === "archive") return <ArchivePreview preview={preview} />;
  if ((preview.type === "pdf" || preview.type === "image") && metadata) return <BinaryInlinePreview key={metadata.id} type={preview.type} metadata={metadata} location={location} revisionId={revisionId} />;
  return <p className="dm-empty">{preview.type === "unsupported" ? preview.message : "Aperçu non disponible"}</p>;
}

function TabularPreview({ preview }: {
  preview: Extract<SourceFilePreview, { type: "csv" | "spreadsheet" }>;
}) {
  const knownRows = preview.totalRows === null ? "inconnu (extrait borné)" : `${preview.totalRows}`;
  const knownColumns = preview.totalColumns === null ? "inconnu (extrait borné)" : `${preview.totalColumns}`;
  const range = preview.displayedRange;
  return <div className="source-preview-table-wrap">
    <dl className="source-preview-summary">
      {preview.type === "spreadsheet" ? <><dt>Feuille</dt><dd>{preview.activeSheet}</dd></> : null}
      {preview.type === "csv" ? <><dt>Encodage</dt><dd>{preview.encoding}</dd></> : null}
      <dt>Lignes connues</dt><dd>{knownRows}</dd>
      <dt>Colonnes</dt><dd>{knownColumns}</dd>
      <dt>Limites</dt><dd>{preview.limits.rows} lignes · {preview.limits.columns} colonnes · {formatSourceBytes(preview.limits.bytes)}</dd>
      <dt>Plage affichée</dt><dd>Lignes {range.rowStart}–{range.rowEnd} · colonnes {range.columnStart}–{range.columnEnd}</dd>
      <dt>Contenu</dt><dd>{preview.truncated ? "Tronqué" : "Complet"}</dd>
    </dl>
    {!preview.rows.length ? <p className="dm-empty">Aucune ligne exploitable.</p> : <div className="source-preview-table-scroll"><table><thead><tr>{preview.columns.map((column, index) => <th key={`${index}-${column}`}>{column || `Colonne ${index + 1}`}</th>)}</tr></thead><tbody>{preview.rows.map((row, rowIndex) => <tr key={rowIndex}>{preview.columns.map((_column, columnIndex) => <td key={columnIndex}>{displayCell(row[columnIndex])}</td>)}</tr>)}</tbody></table></div>}
    {preview.truncated ? <p className="dm-warning">Aperçu limité aux premières lignes, colonnes ou octets.</p> : null}
  </div>;
}

function JsonNode({ value, name }: { value: unknown; name: string }) {
  if (value === null || typeof value !== "object") return <div><strong>{name}</strong>: {displayCell(value)}</div>;
  const entries = Object.entries(value);
  return <details open><summary>{name} ({entries.length})</summary><div className="source-json-children">{entries.map(([key, child]) => <JsonNode key={key} name={key} value={child} />)}</div></details>;
}

function ArchivePreview({ preview }: { preview: Extract<SourceFilePreview, { type: "archive" }> }) {
  return <div><p>{preview.entries.length} entrée(s) sûre(s) · {preview.rejectedEntries} refusée(s)</p><div className="source-archive-list">{preview.entries.map((entry) => <div key={entry.path}><strong>{entry.name}</strong><span>{entry.path}</span><span>{entry.assumedType} · profondeur {entry.depth} · {formatSourceBytes(entry.uncompressedSize)}</span></div>)}</div>{preview.truncated ? <p className="dm-warning">Liste tronquée à {preview.limits.entries} entrées.</p> : null}</div>;
}

function BinaryInlinePreview({ type, metadata, location, revisionId }: {
  type: "pdf" | "image"; metadata: Awaited<ReturnType<typeof getSourceExplorerMetadata>>;
  location: DatasetLocation; revisionId: string;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    void getSourceExplorerContent(location, revisionId, metadata.id, "inline", controller.signal).then((blob) => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch((reason) => {
      if (!controller.signal.aborted) setError(safeErrorMessage(reason));
    });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [location.clientId, location.datasetId, metadata.id, revisionId]);
  if (error) return <p className="dm-warning">{error}</p>;
  if (!url) return <p>Chargement du contenu…</p>;
  if (type === "image") return <img className="source-image-preview" src={url} alt={metadata.name} />;
  return <iframe className="source-pdf-preview" src={url} title={`Aperçu PDF de ${metadata.name}`} />;
}

function Pagination({ offset, limit, total, onPage }: { offset: number; limit: number; total: number; onPage: (offset: number) => void }) {
  if (total === 0) return null;
  if (total <= limit) return <p className="source-explorer-total">{total} élément(s)</p>;
  const end = Math.min(offset + limit, total);
  return <div className="source-explorer-pagination"><span>{offset + 1}–{end} sur {total}</span><div><button className="btn btn-outline btn-sm" type="button" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))}>Précédent</button><button className="btn btn-outline btn-sm" type="button" disabled={end >= total} onClick={() => onPage(offset + limit)}>Suivant</button></div></div>;
}

function ExplorerSkeleton() {
  return <div className="source-explorer-skeleton" aria-label="Chargement"><span /><span /><span /><span /><span /></div>;
}

function PreviewSkeleton({ label }: { label: string }) {
  return <div className="source-preview-skeleton" aria-label={label}><span /><span /><span /></div>;
}

function revisionById(revisions: SourceRevision[], requested: string) {
  const selected = revisions.find((revision) => revision.id === requested);
  if (selected) return selected;
  return revisions.find((revision) => Boolean(revision.id)) ?? null;
}

function updateExplorerParams(
  setter: ReturnType<typeof useSearchParams>[1],
  current: URLSearchParams,
  changes: Record<string, string>,
) {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(changes)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  setter(next, { replace: true });
}

function validOffset(value: string | null) {
  const parsed = Number(value ?? 0);
  if (!Number.isSafeInteger(parsed) || parsed < 0) return 0;
  return parsed;
}

function toggleSelectedFile(setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) {
  setter((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}

function containsDraggedFiles(dataTransfer: DataTransfer) {
  return Array.from(dataTransfer.types).includes("Files");
}

function sourceRoleForFolder(tree: SourceExplorerTree | undefined, folder: string) {
  if (!tree || !folder) return "";
  const role = tree.roles.find((item) => {
    if (item.providedBy !== "user") return false;
    if (folder === item.logicalFolder) return true;
    return folder.startsWith(`${item.logicalFolder}/`);
  });
  return role?.role ?? "";
}

function droppedImportMode(files: File[]): SourceImportSelection["mode"] {
  if (files.some((file) => (file.webkitRelativePath || "").includes("/"))) return "folder";
  if (files.length === 1 && extensionLabel(files[0].name).toLowerCase() === "zip") return "zip";
  return "files";
}

function extensionLabel(filename: string) {
  const position = filename.lastIndexOf(".");
  if (position < 0) return "Fichier";
  return filename.slice(position + 1).toUpperCase();
}

function FileTypeIcon({ filename }: { filename: string }) {
  const extension = extensionLabel(filename).toLowerCase();
  if (["csv", "tsv", "xlsx"].includes(extension)) return <FileSpreadsheet className="source-file-type-icon" size={18} aria-hidden="true" />;
  if (extension === "json") return <FileJson className="source-file-type-icon" size={18} aria-hidden="true" />;
  if (extension === "pdf") return <FileText className="source-file-type-icon" size={18} aria-hidden="true" />;
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(extension)) return <ImageIcon className="source-file-type-icon" size={18} aria-hidden="true" />;
  if (extension === "zip") return <FileArchive className="source-file-type-icon" size={18} aria-hidden="true" />;
  return <File className="source-file-type-icon" size={18} aria-hidden="true" />;
}

function explorerPhysicalFileCount(tree: SourceExplorerTree | undefined) {
  if (!tree) return undefined;
  return tree.folders
    .filter((folder) => folder.parentPath === "")
    .reduce((total, folder) => total + folderPhysicalFileCount(tree, folder), 0);
}

function systemResourceCountForFolder(tree: SourceExplorerTree | undefined, folderPath: string) {
  return explorerSystemResources(tree).filter((resource) => resource.logicalFolder === folderPath).length;
}

function folderCounterLabel(tree: SourceExplorerTree | undefined, folder: SourceExplorerTree["folders"][number]) {
  const fileCount = folderPhysicalFileCount(tree, folder);
  const resourceCount = systemResourceCountForFolder(tree, folder.path);
  if (fileCount && resourceCount) {
    return `${fileCountLabel(fileCount)} · ${systemResourceCountLabel(resourceCount)}`;
  }
  if (fileCount) return fileCountLabel(fileCount);
  if (resourceCount) return systemResourceCountLabel(resourceCount);
  return "Dossier vide";
}

function folderPhysicalFileCount(tree: SourceExplorerTree | undefined, folder: SourceExplorerTree["folders"][number]) {
  if (!tree) return folder.fileCount;
  if (tree.systemResources !== undefined) return folder.fileCount;
  const legacyAdditions = tree.roles.filter((role) => {
    if (role.logicalFolder === folder.path) return true;
    return role.logicalFolder.startsWith(`${folder.path}/`);
  }).length;
  return Math.max(0, folder.fileCount - legacyAdditions);
}

function explorerSystemResources(tree: SourceExplorerTree | undefined) {
  if (!tree) return [];
  if (tree.systemResources !== undefined) return tree.systemResources;
  return tree.roles
    .filter((role) => role.providedBy === "system")
    .map((role) => ({
      id: `system:${role.role}`,
      name: role.label,
      role: role.role,
      logicalFolder: role.logicalFolder,
    }));
}

function fileCountLabel(count: number | undefined) {
  const value = count ?? 0;
  return `${value} fichier${value > 1 ? "s" : ""}`;
}

function systemResourceCountLabel(count: number) {
  return `${count} ressource${count > 1 ? "s" : ""} système${count > 1 ? "s" : ""}`;
}

function revisionFileCount(revision: SourceRevision, selectedId: string, selectedFileCount?: number) {
  if (revision.id === selectedId && selectedFileCount !== undefined) return selectedFileCount;
  return revision.fileCount ?? 0;
}

function revisionStatusLabel(status: string) {
  if (status === "valid") return "validée";
  if (status === "invalid") return "invalide";
  if (status === "semanticized") return "sémantisée";
  if (status === "published") return "publiée";
  if (status === "editing") return "en préparation";
  return status;
}

function displayCell(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return `${value}`;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function safeDownloadName(filename: string) {
  const basename = filename.split(/[\\/]/).pop() ?? "";
  const cleaned = basename.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  if (cleaned) return cleaned.slice(0, 180);
  return "fichier";
}

function storedExplorerScroll(key: string) {
  try {
    const value = Number(window.sessionStorage.getItem(`source-explorer-scroll:${key}`) ?? 0);
    if (Number.isFinite(value) && value >= 0) return value;
  } catch {
    return 0;
  }
  return 0;
}

function storeExplorerScroll(key: string, value: number) {
  try {
    window.sessionStorage.setItem(`source-explorer-scroll:${key}`, `${Math.max(0, Math.round(value))}`);
  } catch {
    // La navigation reste fonctionnelle si le stockage de session est indisponible.
  }
}
